// src/services/admin/adminAwardTemplates.js
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

const adminAwardTemplatesApi = createApi({
  reducerPath: "adminAwardTemplatesApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${process.env.REACT_APP_BACKEND_URL}/api/admin/award-templates`,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem("token");
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ["AwardTemplates", "AwardTemplateVersions"],
  endpoints: (builder) => ({
    // CRUD
    getAllTemplates: builder.query({
      query: () => `/`,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "AwardTemplates", id })),
              { type: "AwardTemplates", id: "LIST" },
            ]
          : [{ type: "AwardTemplates", id: "LIST" }],
    }),
    getTemplateById: builder.query({
      query: (id) => `/${id}`,
      providesTags: (result, error, id) => [{ type: "AwardTemplates", id }],
    }),
    createTemplate: builder.mutation({
      query: (data) => ({
        url: `/`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: [{ type: "AwardTemplates", id: "LIST" }],
    }),
    updateTemplate: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "AwardTemplates", id },
        { type: "AwardTemplates", id: "LIST" },
        { type: "AwardTemplateVersions", id }, // also invalidate version list
      ],
    }),
    deleteTemplate: builder.mutation({
      query: (id) => ({
        url: `/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "AwardTemplates", id },
        { type: "AwardTemplates", id: "LIST" },
      ],
    }),

    // Versioning
    getTemplateVersions: builder.query({
      query: (templateId) => `/${templateId}/versions`,
      providesTags: (result, error, templateId) => [
        { type: "AwardTemplateVersions", id: templateId },
      ],
    }),
    getTemplateVersionById: builder.query({
      query: ({ templateId, versionId }) =>
        `/${templateId}/versions/${versionId}`,
      providesTags: (result, error, { templateId, versionId }) => [
        { type: "AwardTemplateVersions", id: templateId },
        { type: "AwardTemplateVersions", id: versionId },
      ],
    }),
    restoreTemplateVersion: builder.mutation({
      query: ({ templateId, versionId }) => ({
        url: `/${templateId}/versions/${versionId}/restore`,
        method: "POST",
      }),
      invalidatesTags: (result, error, { templateId }) => [
        { type: "AwardTemplates", id: templateId },
        { type: "AwardTemplateVersions", id: templateId },
      ],
    }),
  }),
});

export const {
  useGetAllTemplatesQuery,
  useGetTemplateByIdQuery,
  useCreateTemplateMutation,
  useUpdateTemplateMutation,
  useDeleteTemplateMutation,
  useGetTemplateVersionsQuery,
  useGetTemplateVersionByIdQuery,
  useRestoreTemplateVersionMutation,
} = adminAwardTemplatesApi;

export default adminAwardTemplatesApi;
export { adminAwardTemplatesApi };
